import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Col, Form, Row, Button, Container, Spinner } from "react-bootstrap";
import "./WorkItemUpdate.css";

import { toast } from "react-toastify";
import moment from "moment";
import { useProperty } from "../../data/PropertyHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import useLogger from "../../util/Logger";
import { useAppointmentSlots } from "../../data/AppointmentSlotHook";
import { useWorkItemAgreements } from "../../data/WorkItemAgreementsHook";
import { useWorkItemCategories } from "../../data/WorkItemCategoriesHook";
import { useProjects } from "../../data/ProjectsHook";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import {
  isWorkOrderClosed,
  isWorkOrderItemClosed,
} from "../../data/WorkOrderStatus";
import WorkOrderAddress from "../workOrder/WorkOrderAddress";
import { useWorkOrderItems } from "../../data/WorkItemHook";
import WorkItemEdit from "./WorkItemEdit";
import { useWorkItemTemplates } from "../../data/WorkItemTemplatesHook";
import SpinnerButton from "../shared/Spinnerbutton";

function WorkItemUpdate() {
  const { propertyId, workOrderId, workItemId } = useParams();
  const propertyRepository = useProperty();
  const [property, setProperty] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const workOrdersRepository = useWorkOrders();
  const workItemsRepository = useWorkOrderItems();
  const logger = useLogger();
  const appointmentSlotsRepository = useAppointmentSlots();
  const workItemAgreementsRepository = useWorkItemAgreements();
  const workItemCategoryRepository = useWorkItemCategories();
  const breadcrumbRepository = useBreadcrumb();
  const workItemTemplatesRepository = useWorkItemTemplates();
  const navigate = useNavigate();
  const projectsRepository = useProjects();

  useEffect(() => {
    const runMe = async () => {
      try {
        logger.debug("WorkItemUpdate.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}`,
            name: "work order",
          },
          {
            route: `/property/${propertyId}/workOrder/${workOrderId}/workItem/${workItemId}/update`,
            name: "update work item",
          },
        ]);

        const [
          retrievedProperty,
          appointmentSlots,
          workItemCategories,
          templates,
        ] = await Promise.all([
          propertyRepository.getProperty(propertyId),
          appointmentSlotsRepository.updateAppointmentSlots(),
          workItemCategoryRepository.updateWorkItemCategories(),
          workItemTemplatesRepository.getWorkItemTemplates([
            "DomesticFieldReactive",
            "DomesticCMV",
            "Disconnection",
            "Reconnection",
            "DomesticProject-PropertyAsset",
            "DomesticProject-PropertyInstallation",
            "DomesticProject-PropertyOptimiseException",
            "DomesticProject-PropertyMeterException"
          ]),
          workItemAgreementsRepository.updateWorkItemAgreements(),
          projectsRepository.updateProjects(),
        ]);

        setProperty(retrievedProperty);

        const defaultAppointmentSlot = appointmentSlots.find(
          (appointmentSlot) => appointmentSlot.id === "PATS001"
        );

        const retrievedWorkOrder =
          await workOrdersRepository.getWorkOrderForUpdate(
            propertyId,
            workOrderId,
            workItemCategories
          );

        if (!retrievedWorkOrder)
          throw new Error(
            `Failed to load work order for property '${retrievedProperty.id}' and work order '${workOrderId}'`
          );

        if (isWorkOrderClosed(retrievedWorkOrder)) {
          throw new Error(
            `Illegal attempt to edit work item for closed work order for property '${retrievedProperty.id}' and work order '${workOrderId}'`
          );
        }

        const workItem = retrievedWorkOrder.workOrderItems.find(
          (element) => element.id === workItemId
        );
        if (!workItem)
          throw new Error(
            `Failed to load work order item for property '${retrievedProperty.id}' and work order '${workOrderId} and work order item '${workItem}'`
          );

        if (isWorkOrderItemClosed(workItem)) {
          throw new Error(
            `Illegal attempt to edit closed work item for property '${retrievedProperty.id}' and work order '${workOrderId}' and work order item '${workItem}'`
          );
        }

        const template = templates.find(
          (t) => t.name === workItem.workitemtemplatedisplayname
        );

        if (!template) {
          throw new Error(
            `Illegal attempt to edit disallowed work item template for property '${
              retrievedProperty.id
            }' and work order '${workOrderId}' and work order item '${JSON.stringify(
              workItem
            )}'`
          );
        }

        setWorkOrder(retrievedWorkOrder);

        // Set the work item
        const workOrderItem = retrievedWorkOrder.workOrderItems.find(
          (element) => element.id === workItemId
        );

        workItemsRepository.setEntry({
          ...workOrderItem,
          workitemtemplateref: template.ref,
          preferredAppointmentSlot:
            workOrderItem.preferredAppointmentSlot || defaultAppointmentSlot.id,
          preferredAppointmentSlotTitle:
            workOrderItem.preferredAppointmentSlotTitle ||
            defaultAppointmentSlot.name,
        });

        setIsLoading(false);
      } catch (err) {
        logger.error(
          "WorkItemUpdate.useEffect",
          `An error occurred while loading work order '${workOrderId}' ${err}`
        );
        toast.error(
          "An error occurred while loading the work item. Please try again."
        );
        navigate("/");
      }
    };
    runMe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    try {
      setIsSaving(true);

      const form = event.currentTarget;
      event.preventDefault();
      event.stopPropagation();

      const current = workItemsRepository.getEntry();
      if (form.checkValidity() === false) {
        setValidated(true);
        toast.error(
          "There are errors on your form. Please check each field and try again."
        );
      } else if (
        !current.preferredAppointmentDate &&
        current.workitemtemplateref !== "Disconnection"
      ) {
        setValidated(true);
        toast.error("Please select a date from the calendar.");
      } else {
        // Save the work order item
        await workItemsRepository.updateWorkItem(current);

        toast.success("The Work Item was updated successfully.");
        navigate(`/property/${propertyId}/workOrder/${workOrderId}`);
      }
    } catch (err) {
      logger.error(
        "WorkItemUpdate.handleSubmit",
        `API patch work item failed ${err}`
      );
      toast.error("The work item could not be updated. Please try again.");
    }
    setIsSaving(false);
  };

  const workOrderItem = workItemsRepository.getEntry();

  return !isLoading && workOrder && workOrderItem ? (
    <Container className="coreContent">
      <h2>Update Work Item</h2>
      <WorkOrderAddress property={property} />

      <Row className="mt-4">
        <Col xs={3}>
          <Form.Label className="form-label-heading">
            Work Order Reference
          </Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Created</Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Type</Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="form-label-heading">Category</Form.Label>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.reference} />
        </Col>
        <Col xs={3}>
          <Form.Control
            type="text"
            readOnly
            value={
              workOrder.createdOn
                ? moment(workOrder.createdOn).format("DD/MM/YYYY h:mm a")
                : ""
            }
          />
        </Col>
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.type} />
        </Col>
        <Col xs={3}>
          <Form.Control type="text" readOnly value={workOrder.categoryName} />
        </Col>
      </Row>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <div className="entry-container">
          <WorkItemEdit editing />
        </div>

        <div className="work-item-update-buttons-container">
          <Row xs="auto">
            <Col className="work-item-update-button">
              <Link to={`/property/${property.id}/workorder/${workOrderId}`}>
                <Button variant="secondary">Back to work order</Button>
              </Link>
            </Col>
            <Col>
              <SpinnerButton
                text="Save"
                variant="primary"
                action={() => document.getElementById("updateSubmit").click()}
                waiting={isSaving}
              />
            </Col>
            <Col>
              <Button
                id="updateSubmit"
                type="submit"
                style={{ visibility: "hidden" }}
              >
                Save
              </Button>
            </Col>
          </Row>
        </div>
      </Form>
    </Container>
  ) : (
    <Container className="coreContent">
      <Spinner
        animation="border"
        role="status"
        style={{
          marginTop: "200px",
          marginLeft: "50%",
          marginBottom: "200px",
        }}
      >
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </Container>
  );
}

export default WorkItemUpdate;
